/**
 * AddToMailshotModal Component
 *
 * Reusable modal for adding properties to existing mailshots.
 * To create new mailshots, use the campaign flow.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { MailshotRepository } from "../../models/MailshotRepository";
import { useToast } from "./Toast";
import { FaEnvelope, FaTimes, FaSpinner } from "react-icons/fa";
import "./AddToMailshotModal.scss";
import { mailabilityOf, HELD_BACK_LABELS } from "../../models/mailability";
import { Checkbox, Input } from "./Form";
import InfoNotice from "./InfoNotice";
import { AddCheckReview } from "./AddCheckReview";
import { Button } from "./Button";
import { developmentChoice, developmentNote, flaggedAmong, withoutFlagged, flaggedRow, notReadWords } from "../../utils/developmentChoice";
import { goneQuoteChoice, goneQuoteNote, goneQuoteHomesAmong, withoutGoneQuoteHomes, goneQuoteRow } from "../../utils/goneQuoteChoice";
import { checkKey, chooseClass, settled, checkedWords, uncheckedWords, unaccounted, doNotContactIds, addTimeWords, sentChoice, sentHomes, SENT_BY_RECORD, NOT_STAMPED_WORDS } from "../../utils/addCheckFlow";
import { isMailshotSent } from "../../models/mailshotSent";
import {
  STATUS_CHOICES,
  OTHER_STATUS,
  allStatusChoices,
  countByStatusChoice,
  filterByStatusChoices,
  toggleStatusChoice,
  noPriceNote,
} from "../../utils/statusChoices";
import { ownFigure, openingText, limitOf, toSend, keepsForPrice, priceWords, priceNote, leftOutByAdd, figureText } from "../../utils/priceLimit";

export const AddToMailshotModal = ({
  isOpen,
  onClose,
  propertyIds = [], // Deprecated: use properties instead
  properties = [], // New: array of property objects with full data
  onSuccess,
}) => {
  const toast = useToast();
  const [mailshots, setMailshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMailshot, setSelectedMailshot] = useState(null);
  const [adding, setAdding] = useState(false);
  // Which sale statuses to add: any mix (For Sale, Under Offer, SSTC), all ticked to start,
  // which is what "All" used to mean. See utils/statusChoices.js.
  const [statusChoices, setStatusChoices] = useState(allStatusChoices);
  const [includeMediumConfidence, setIncludeMediumConfidence] = useState(false);
  // v2 high + single evidence is under evaluation, so the default mailing is
  // high + STRONG only. See models/mailability.js for the whole rule.
  const [includeSingleEvidence, setIncludeSingleEvidence] = useState(false);
  const ruleOpts = { includeSingleEvidence, includeMediumConfidence };
  const [baseMaxVal, setBaseMaxVal] = useState(null);
  // THE PRICE LIMIT (utils/priceLimit.js). It opens as it always has: ticked, at the figure Tools holds (the copy of
  // the settings behind the Config page, NOT proven to be the quote journey's own, and never written here). A person
  // may type another figure, whole pounds only; one that is not is refused in words and the add is not offered,
  // because the add function applies whatever figure this page sends. null = not touched since the box opened.
  const [priceOnChoice, setPriceOnChoice] = useState(null);
  const [priceTyped, setPriceTyped] = useState(null);
  const ownPrice = ownFigure(baseMaxVal);
  const priceOn = priceOnChoice === null ? ownPrice.held : priceOnChoice;
  const priceText = priceTyped === null ? openingText(ownPrice) : priceTyped;
  const priceLimit = limitOf(priceOn, priceText);
  const priceBlocked = priceLimit.on && !priceLimit.ok;
  // Homes flagged as a development are left out unless this is unticked (EST-192; Lee, 9 Oct 2026: "we don't want
  // new homes"). ⚠️ The box takes them out of the selection BEFORE the check is asked, as the status tick-boxes do: so
  // it holds whether or not the check is made, and the check is only ever asked of homes that would go in. It needs
  // the homes' records: where the box is given ids only it cannot read the flag, and says so. utils/developmentChoice.js.
  const [leaveOutDevelopments, setLeaveOutDevelopments] = useState(true);
  const [showDevelopments, setShowDevelopments] = useState(false);
  // Homes brought in FROM A QUOTE that HubSpot no longer has (deleted or merged away; EST-230) are left out unless this
  // is unticked, taken out of the selection BEFORE the check, as the developments are. utils/goneQuoteChoice.js.
  const [leaveOutGoneQuotes, setLeaveOutGoneQuotes] = useState(true);
  const [showGoneQuotes, setShowGoneQuotes] = useState(false);
  // The check before the add (EST-169): which of these homes read as one already in the
  // mailshot, or as another in this selection, and which match a do-not-contact record by
  // address. ⚠️ It is THIS PAGE's check: the add function does not make it. On by default;
  // unticked, or if the check cannot be made, everything is added as it always was.
  const [checkFirst, setCheckFirst] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkAnswer, setCheckAnswer] = useState(null);
  const [checkedAs, setCheckedAs] = useState(null);
  const [checkProblem, setCheckProblem] = useState(null);
  const [choices, setChoices] = useState({});
  // Homes a check has FOUND to match do not contact stay out for as long as the box is open,
  // whatever is done with the tick-box afterwards: a person who has just been shown the match
  // must not be able to add it by unticking the check.
  const [knownDoNotContact, setKnownDoNotContact] = useState([]);
  // HOMES ALREADY IN A MAILSHOT THAT HAS BEEN SENT (Lee's rule of 8 Oct 2026). The check says which; they are left
  // out unless this is unticked. ⚠️ By record, by the mailshots' own lists: the same home sent before under another
  // listing is not caught, and the box says so. A home the check has named stays known for as long as the box is
  // open, for the mailshot it was asked of, and is left out of ANY add while this is ticked: unticking the check
  // afterwards must not be a way to send a second letter unseen.
  const [leaveOutSent, setLeaveOutSent] = useState(true);
  const [showSent, setShowSent] = useState(false);
  const [knownSent, setKnownSent] = useState({});

  // The homes the status, mailing-rule and price choices would add, BEFORE the development tick-box. That tick-box's
  // count, its "Show them" list and the "left out" figure in the message after the add are all of THIS one set.
  // The homes the status and mailing-rule choices keep, BEFORE the price limit: what the limit's count is "of".
  const beforePrice = () => {
    // Apply status filter
    const filtered = filterByStatusChoices(properties, statusChoices);

    // The mailing rule — the SAME one the server enforces (models/mailability.js).
    // The inline copy this replaced let v1 medium through with "include medium"
    // ticked, which the scraper briefing says must never be mailed.
    return filtered.filter((p) => mailabilityOf(p, ruleOpts).mailable);
  };
  const hasRecords = properties.length > 0;
  const inHand = hasRecords ? beforePrice() : [];
  // the price limit: the test it has always been, at the figure in the box (no figure: every home is kept)
  const chosen = inHand.filter((p) => keepsForPrice(p, priceLimit.figure));
  const price = priceWords({ limit: priceLimit, own: ownPrice, hadRecords: hasRecords, inHand, idsCount: propertyIds.length });
  const development = developmentChoice(chosen, leaveOutDevelopments);
  const flaggedHomes = flaggedAmong(chosen);
  const goneQuote = goneQuoteChoice(chosen, leaveOutGoneQuotes);
  const goneQuoteHomes = goneQuoteHomesAmong(chosen);

  // Determine which property source to use and apply filtering
  const getFilteredPropertyIds = () => {
    // Use properties array if provided, otherwise fall back to propertyIds
    if (hasRecords) {
      let keep = leaveOutDevelopments ? withoutFlagged(chosen) : chosen;
      if (leaveOutGoneQuotes) keep = withoutGoneQuoteHomes(keep);
      return keep.map((p) => p.id);
    }

    // Fallback to old behavior: ids only, so nothing here can be read of the homes (not their status, not the flag)
    return propertyIds;
  };

  const filteredPropertyIds = getFilteredPropertyIds();

  const getPropertyCountsByStatus = () => {
    if (properties.length === 0) {
      return countByStatusChoice([]);
    }

    // Same rule as the list above and the server — one answer everywhere.
    let filtered = properties.filter((p) => mailabilityOf(p, ruleOpts).mailable);

    // the price limit, by the same test as the list above
    filtered = filtered.filter((p) => keepsForPrice(p, priceLimit.figure));

    return countByStatusChoice(filtered);
  };

  const counts = getPropertyCountsByStatus();

  useEffect(() => {
    if (isOpen) {
      fetchMailshots();
      setStatusChoices(allStatusChoices()); // Reset to every status when opening
      setIncludeMediumConfidence(false); // Default OFF for quality control
      setPriceOnChoice(null); // as it opens: ticked when Tools holds a figure…
      setPriceTyped(null); // …and at that figure, whatever was typed last time
      setLeaveOutDevelopments(true); // Default ON — leave out homes flagged as a development
      setShowDevelopments(false);
      setLeaveOutGoneQuotes(true); // Default ON — leave out homes brought in from a quote HubSpot no longer has
      setShowGoneQuotes(false);
      setCheckFirst(true); // Default ON — check for doubles and do not contact first
      setCheckAnswer(null);
      setCheckedAs(null);
      setCheckProblem(null);
      setChoices({});
      setKnownDoNotContact([]);
      setLeaveOutSent(true); // Default ON — leave out homes already in a mailshot that has been sent
      setShowSent(false);
      setKnownSent({});
      setChecking(false);
    }
  }, [isOpen]);

  const mailshotRepository = useMemo(
    () => new MailshotRepository(db, functions),
    []
  );

  const fetchMailshots = async () => {
    try {
      setLoading(true);
      // Domain read via repository; config/settings is a singleton settings doc
      // (no domain repository) so it stays a direct getDoc.
      const [mailshotsData, configDoc] = await Promise.all([
        mailshotRepository.findAll({ orderBy: "createdAt", orderDirection: "desc" }),
        getDoc(doc(db, "config", "settings")),
      ]);
      setMailshots(mailshotsData);
      if (configDoc.exists()) {
        const val = configDoc.data()?.pricing?.baseMaxVal;
        setBaseMaxVal(val || null);
      }
    } catch (error) {
      console.error("Error fetching mailshots:", error);
    } finally {
      setLoading(false);
    }
  };

  // A check answers for one mailshot and one list of homes: change either and it is out of date.
  const currentKey = checkKey(selectedMailshot, filteredPropertyIds);
  const checked = checkFirst && checkAnswer && checkedAs === currentKey ? checkAnswer : null;
  const toAdd = checked ? settled(checked, choices, { leaveOutSent }) : null;
  // the homes the box for "already sent" is about: those the check would otherwise add (ONE set: its number, its
  // list and the figure in the message). null when the check's answer does not say which homes (an older function).
  const sent = checked ? sentChoice(checked, choices, leaveOutSent) : null;
  // what the box is showing NOW, for an answer that comes back after the box has moved on
  const latest = useRef({ key: currentKey, open: isOpen });
  latest.current = { key: currentKey, open: isOpen };
  // Is the box still on the page at all? `latest` is only brought up to date when the box is DRAWN: a box its page
  // has taken away (Escape on the import's window, or leaving the page) is never drawn again, so it would go on
  // reading "open". A person who pressed Escape meant "do not".
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // everything but the homes a check has already found to match do not contact, and (while its box is ticked) the
  // homes a check of THIS mailshot has already found in a mailshot that has been sent
  const sentKnownHere = knownSent[selectedMailshot] || [];
  const isKnownDnc = (id) => knownDoNotContact.includes(String(id));
  const isKnownSent = (id) => leaveOutSent && sentKnownHere.includes(String(id));
  const withoutKnown = (ids) => ids.filter((id) => !isKnownDnc(id) && !isKnownSent(id));
  const keptOut = filteredPropertyIds.filter(isKnownDnc).length;
  const sentKeptOut = filteredPropertyIds.filter((id) => !isKnownDnc(id) && isKnownSent(id)).length;
  // how many of these homes an earlier check named as sent, whatever the box says now: so that the box can be shown
  const sentKnownNow = filteredPropertyIds.filter((id) => !isKnownDnc(id) && sentKnownHere.includes(String(id))).length;
  // how many such homes go in because that box is unticked: the check HAD told, so the message says so
  const sentAddedUnticked = leaveOutSent ? 0 : sentKnownNow;
  // something for a person to look at before anything is added. ⚠️ Of homes already in a sent mailshot, only those
  // that WOULD be added: one that is already in this mailshot is left out anyway, and stopped the first click
  // with nothing to decide (the second reviewer's N3).
  const needsALook = (answer) => answer.check.counts.listed > 0 || (answer.missing || []).length > 0 || (answer.check.unsearched || []).length > 0 || ((sentChoice(answer, {}, true) || {}).count || 0) > 0 || settled(answer, {}, { leaveOutSent }).ids.length === 0;

  const handleAddToMailshot = async () => {
    if (!selectedMailshot) {
      toast.warning("Please select a mailshot");
      return;
    }
    if (checking || adding) return;
    if (priceBlocked) {
      toast.warning("The top price is not a whole number of pounds: put it right, or untick the limit, before adding.");
      return;
    }
    if (!checkFirst) return addIds(withoutKnown(filteredPropertyIds), uncheckedWords("off", keptOut, sentKeptOut, sentAddedUnticked));
    if (checked) return addIds(toAdd.ids, checkedWords(checked, choices, { leaveOutSent }));

    // Ask the check. With nothing to decide, go straight on to the add: one click, as before.
    const askedKey = currentKey; const askedIds = filteredPropertyIds; const askedMailshot = selectedMailshot;
    let answer;
    try {
      setChecking(true);
      setCheckProblem(null);
      const checkMailshotAdd = httpsCallable(functions, 'checkMailshotAdd', { timeout: 130000 });
      answer = (await checkMailshotAdd({ mailshotId: selectedMailshot, propertyIds: askedIds })).data;
      // an answer that does not account for every home asked of is a failed check, not "no doubles"
      const wrong = unaccounted(answer, askedIds);
      if (wrong) throw new Error(wrong);
      setCheckAnswer(answer);
      setCheckedAs(askedKey);
      setChoices({});
      setKnownDoNotContact((known) => [...new Set([...known, ...doNotContactIds(answer)])]);
      // which homes it named as already in a sent mailshot, kept for THIS mailshot ("sent elsewhere" is asked of one mailshot)
      setKnownSent((known) => ({ ...known, [askedMailshot]: [...new Set([...(known[askedMailshot] || []), ...(sentHomes(answer) || []).map((h) => h.id)])] }));
    } catch (error) {
      console.error("Error checking before adding to mailshot:", error);
      setCheckProblem(error.message || "no reason was given");
      return;
    } finally {
      setChecking(false);
    }
    // the box was closed or taken off the page, or shows another mailshot or selection, while the check ran: this click adds nothing
    if (!mounted.current || !latest.current.open || latest.current.key !== askedKey) return;
    if (!needsALook(answer)) return addIds(settled(answer, {}, { leaveOutSent }).ids, checkedWords(answer, {}, { leaveOutSent }));
  };

  /** One home of the "already sent" list: its address where the box has the record, the sent mailshots that hold it, and its own page. */
  const sentRow = (home) => {
    const record = properties.find((p) => String(p.id) === home.id);
    const row = record ? flaggedRow(record) : { id: home.id, address: `home ${home.id}`, href: `/dashboard/properties/property/${encodeURIComponent(home.id)}` };
    return { ...row, mailshots: home.mailshots, notStamped: home.notStamped || [] };
  };

  const addIds = async (ids, checkedNote) => {
    // the add function applies whatever figure this page sends: a figure that is not whole pounds is never sent
    if (priceBlocked) return;
    if (ids.length === 0) {
      toast.warning("There is nothing to add: every home was left out.");
      return;
    }

    try {
      setAdding(true);

      // Call Cloud Function which handles:
      // - Validation
      // - Duplicate campaign send checks
      // - Banned property filtering (CRITICAL)
      // - Confidence level filtering
      // - Adding properties to mailshot
      // - Updating cached counts
      // - Updating scrape stats
      // All in atomic transactions
      const addPropertiesToMailshot = httpsCallable(functions, 'addPropertiesToMailshot');
      const result = await addPropertiesToMailshot({
        mailshotId: selectedMailshot,
        propertyIds: ids,
        includeMediumConfidence: includeMediumConfidence,
        includeSingleEvidence: includeSingleEvidence,
        ...toSend(priceLimit),
      });


      const mailshot = mailshots.find((m) => m.id === selectedMailshot);
      // what the development tick-box did, of the same set the box showed
      // how many of the flagged homes were among those SENT to be added: unticked, the check may still have left some out
      const flaggedIds = new Set(flaggedHomes.map((p) => String(p.id)));
      const flaggedSent = new Set(ids.map(String).filter((id) => flaggedIds.has(id))).size;
      // what the price limit did, of the same homes the box counted; and what the add itself left out for price
      const priceAfter = priceNote({ limit: priceLimit, own: ownPrice, hadRecords: hasRecords, inHand, byAdd: leftOutByAdd(result.data) });
      const goneIds = new Set(goneQuoteHomes.map((p) => String(p.id)));
      const goneSent = new Set(ids.map(String).filter((id) => goneIds.has(id))).size;
      const goneQuoteWords = goneQuoteNote({ hadRecords: hasRecords, leaveOut: leaveOutGoneQuotes, gone: goneQuote.gone, goneSent });
      const developmentWords = developmentNote({ hadRecords: hasRecords, leaveOut: leaveOutDevelopments, flagged: development.flagged, flaggedSent, noFlag: development.noFlag, noFlagImported: development.noFlagImported, noFlagNeverScraped: development.noFlagNeverScraped });
      
      if (onSuccess) {
        // ⚠️ Name what was held back and WHY. A bare "added 312" when 400 were
        // selected leaves the operator guessing which switch to flip — and a
        // silent shortfall on a mailing is how a surprise becomes a discovery.
        const held = Object.entries(result.data.excludedByReason || {})
          .filter(([, n]) => n > 0)
          .map(([r, n]) => `${n} ${HELD_BACK_LABELS[r] || r}`);
        if (result.data.shadowExcluded) {
          held.push(`${result.data.shadowExcluded} ${HELD_BACK_LABELS.shadow}`);
        }
        onSuccess(
          `Added ${result.data.added} ${
            result.data.added === 1 ? "property" : "properties"
          } to "${mailshot.name}"` + (held.length ? ` — held back: ${held.join(", ")}` : "") + (priceAfter ? ` — ${priceAfter}` : "") + (checkedNote ? ` — ${checkedNote}` : "") + (goneQuoteWords ? ` — ${goneQuoteWords}` : "") + (developmentWords ? ` — ${developmentWords}` : "")
        );
      }

      onClose();
    } catch (error) {
      console.error("Error adding to mailshot:", error);
      // Extract user-friendly error message
      const errorMessage = error.message || "Failed to add properties to mailshot";
      toast.error(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  // The price limit's control: drawn once the settings have been read, with records or with ids only (with ids only
  // the limit was applied at the add with nothing on the box to say so, or to untick).
  const smallLine = { fontSize: "0.8125rem", color: "#475569", margin: 0 };
  const priceControl = !loading && (
    <div
      className="mailshot-modal__filter"
      data-testid="price-limit"
      style={{ marginTop: "0.75rem", display: "flex", alignItems: "flex-start", gap: "0.35rem", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <Checkbox
          checked={priceOn}
          onChange={(e) => setPriceOnChoice(e.target.checked)}
          label="Limit to a top price of £"
          disabled={adding || checking}
        />
        <Input
          value={priceText}
          onChange={(e) => setPriceTyped(e.target.value)}
          onBlur={() => { if (priceTyped !== null && priceLimit.on && priceLimit.ok) setPriceTyped(figureText(priceLimit.figure)); }}
          inputMode="numeric"
          size="small"
          error={priceBlocked}
          disabled={!priceOn || adding || checking}
          aria-label="Top price in pounds"
          data-testid="price-limit-figure"
          style={{ width: "9rem" }}
        />
        <span style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>{price.hint}</span>
      </div>
      {price.refused && <InfoNotice variant="warning" data-testid="price-refused">{price.refused}</InfoNotice>}
      {price.upTo && <p data-testid="price-up-to" style={smallLine}>{price.upTo}</p>}
      {price.differs && (price.differsKind === "above"
        ? <InfoNotice variant="warning" data-testid="price-differs">{price.differs}</InfoNotice>
        : <p data-testid="price-differs" style={smallLine}>{price.differs}</p>)}
      {price.unreadable && <InfoNotice variant="warning" data-testid="price-unreadable">{price.unreadable}</InfoNotice>}
      {price.off && <InfoNotice variant="warning" data-testid="price-off">{price.off}</InfoNotice>}
      {/* a home with no price passes the limit; said, because an import's homes have none */}
      {noPriceNote(chosen, priceLimit.figure !== null) && (
        <p data-testid="no-price-note" style={smallLine}>{noPriceNote(chosen, priceLimit.figure !== null)}</p>
      )}
    </div>
  );
  // not while a check or an add is on its way: the answer would land on a box that had gone, or moved to another mailshot
  const close = () => { if (checking || adding) return; onClose(); };

  return (
    <div className="mailshot-modal-overlay" onClick={close}>
      <div className="mailshot-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mailshot-modal__header">
          <div className="mailshot-modal__title">
            <FaEnvelope />
            <span>Add to Mailshot</span>
          </div>
          <button className="mailshot-modal__close" onClick={close} disabled={checking || adding} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="mailshot-modal__body">
          {properties.length > 0 && (
            <>
              {/* Any mix of statuses in one go: SSTC and Under Offer without For Sale no
                  longer means adding twice. "Other" (Off Market, or no status) is shown
                  only when there are some. */}
              <div
                className="mailshot-modal__filter"
                role="group"
                aria-label="Property status"
                style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}
              >
                <span>Property Status:</span>
                {STATUS_CHOICES.filter((c) => c.value !== OTHER_STATUS || counts[OTHER_STATUS] > 0).map((c) => (
                  <Checkbox
                    key={c.value}
                    checked={statusChoices.includes(c.value)}
                    onChange={() => setStatusChoices((chosen) => toggleStatusChoice(chosen, c.value))}
                    label={`${c.label} (${counts[c.value]})`}
                  />
                ))}
              </div>

              {/* ⚠️ The default mailing is high confidence + STRONG evidence (two
                  or more sales matched). Single-sale matches are under evaluation
                  from Lee's spot-checks, so they are opt-in, never silent. */}
              <div
                className="mailshot-modal__filter"
                style={{
                  marginTop: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <label
                  htmlFor="include-single-evidence"
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}
                >
                  <input
                    type="checkbox"
                    id="include-single-evidence"
                    checked={includeSingleEvidence}
                    onChange={(e) => setIncludeSingleEvidence(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  <span>Include single-sale matches</span>
                </label>
                <span
                  style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}
                  title="High confidence, but only one sale in the property's history matched. Off by default while these are evaluated."
                >
                  (Off by default — strong evidence only)
                </span>
              </div>

              <div 
                className="mailshot-modal__filter" 
                style={{ 
                  marginTop: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <label 
                  htmlFor="include-medium-confidence"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    fontSize: "0.875rem"
                  }}
                >
                  <input
                    type="checkbox"
                    id="include-medium-confidence"
                    checked={includeMediumConfidence}
                    onChange={(e) => setIncludeMediumConfidence(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  <span>Include medium confidence matches</span>
                </label>
                <span 
                  style={{ 
                    fontSize: "0.75rem", 
                    color: "#6b7280",
                    fontStyle: "italic"
                  }}
                >
                  (Off by default for quality control)
                </span>
              </div>

              {priceControl}

              {/* Homes flagged as a development (EST-192): left out unless unticked, and taken out of the selection
                  BEFORE the check is asked. Three answers, never two: flagged, not flagged, and no flag recorded. */}
              <div className="mailshot-modal__filter" data-testid="development-choice" style={{ marginTop: "0.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Checkbox
                    checked={leaveOutDevelopments}
                    onChange={(e) => setLeaveOutDevelopments(e.target.checked)}
                    label={development.label}
                    disabled={adding || checking}
                  />
                  <span style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>{development.hint}</span>
                  {development.flagged > 0 && (
                    <Button variant="link" size="small" aria-expanded={showDevelopments} onClick={() => setShowDevelopments((shown) => !shown)}>
                      {showDevelopments ? "Hide them" : "Show them"}
                    </Button>
                  )}
                </div>
                {development.untickedWords && (
                  <InfoNotice variant="warning" data-testid="development-unticked">{development.untickedWords}</InfoNotice>
                )}
                {development.noFlagWords && (
                  <p data-testid="development-no-flag" style={{ fontSize: "0.8125rem", color: "#475569", margin: "0.35rem 0 0" }}>{development.noFlagWords}</p>
                )}
                {development.importedWords && (
                  <p data-testid="development-imported" style={{ fontSize: "0.8125rem", color: "#475569", margin: "0.35rem 0 0" }}>{development.importedWords}</p>
                )}
                {development.neverScrapedWords && (
                  <p data-testid="development-never-scraped" style={{ fontSize: "0.8125rem", color: "#475569", margin: "0.35rem 0 0" }}>{development.neverScrapedWords}</p>
                )}
                {showDevelopments && development.flagged > 0 && (
                  <ul data-testid="development-list" style={{ fontSize: "0.8125rem", margin: "0.35rem 0 0", paddingLeft: "1.1rem", maxHeight: "9rem", overflow: "auto" }}>
                    {flaggedHomes.map(flaggedRow).map((row, i) => (
                      <li key={row.id === null ? `no-id-${i}` : row.id}>
                        {row.address} <span style={{ color: "#6b7280" }}>(flagged by {row.by})</span>{" "}
                        {row.href
                          ? <a href={row.href} target="_blank" rel="noopener noreferrer">open its page</a>
                          : <span style={{ color: "#6b7280" }}>this record has no id, so it has no page to open</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Homes brought in from a quote HubSpot no longer has (EST-230): left out unless unticked, BEFORE the
                  check. Shown only when there are some. */}
              {goneQuote.gone > 0 && (
                <div className="mailshot-modal__filter" data-testid="gone-quote-choice" style={{ marginTop: "0.75rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <Checkbox
                      checked={leaveOutGoneQuotes}
                      onChange={(e) => setLeaveOutGoneQuotes(e.target.checked)}
                      label={goneQuote.label}
                      disabled={adding || checking}
                    />
                    <span style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>{goneQuote.hint}</span>
                    <Button variant="link" size="small" aria-expanded={showGoneQuotes} onClick={() => setShowGoneQuotes((shown) => !shown)}>
                      {showGoneQuotes ? "Hide them" : "Show them"}
                    </Button>
                  </div>
                  {goneQuote.untickedWords && (
                    <InfoNotice variant="warning" data-testid="gone-quote-unticked">{goneQuote.untickedWords}</InfoNotice>
                  )}
                  {showGoneQuotes && (
                    <ul data-testid="gone-quote-list" style={{ fontSize: "0.8125rem", margin: "0.35rem 0 0", paddingLeft: "1.1rem", maxHeight: "9rem", overflow: "auto" }}>
                      {goneQuoteHomes.map(goneQuoteRow).map((row, i) => (
                        <li key={row.id === null ? `no-id-${i}` : row.id}>
                          {row.address}{" "}
                          {row.href
                            ? <a href={row.href} target="_blank" rel="noopener noreferrer">open its page</a>
                            : <span style={{ color: "#6b7280" }}>this record has no id, so it has no page to open</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}

          {!hasRecords && propertyIds.length > 0 && priceControl}

          {!hasRecords && propertyIds.length > 0 && (
            <InfoNotice variant="info" data-testid="development-not-read">{notReadWords(propertyIds.length)}</InfoNotice>
          )}
          {!hasRecords && propertyIds.length > 0 && (
            <p data-testid="gone-quote-not-read" style={{ fontSize: "0.8125rem", color: "#475569", margin: "0.35rem 0 0" }}>Nor were they read for a quote HubSpot no longer has: a home brought in from such a quote would be added with the rest.</p>
          )}

          {priceBlocked ? (
            <p className="mailshot-modal__subtitle" data-testid="adding-not-worked-out">
              How many would be added is not worked out until the top price is put right.
            </p>
          ) : (
            <p className="mailshot-modal__subtitle" data-testid="adding-count">
              Adding {filteredPropertyIds.length}{" "}
              {filteredPropertyIds.length === 1 ? "property" : "properties"}
            </p>
          )}

          {loading ? (
            <div className="mailshot-modal__loading">
              <FaSpinner className="spinner" />
              <p>Loading mailshots...</p>
            </div>
          ) : (
            <div className="mailshot-modal__section">
              <h4>Select a Mailshot</h4>
              {mailshots.length === 0 ? (
                <div className="mailshot-modal__empty">
                  <p>No mailshots available yet.</p>
                  <p className="mailshot-modal__hint">
                    To create new mailshots, go to the Campaigns tab and create
                    a campaign first.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mailshot-list">
                    {mailshots.map((mailshot) => (
                      <label key={mailshot.id} className="mailshot-item">
                        <input
                          type="radio"
                          name="mailshot"
                          value={mailshot.id}
                          checked={selectedMailshot === mailshot.id}
                          onChange={(e) => setSelectedMailshot(e.target.value)}
                          disabled={checking || adding}
                        />
                        <div className="mailshot-item__content">
                          <div className="mailshot-item__name">
                            {mailshot.name}
                          </div>
                          <div className="mailshot-item__meta">
                            {mailshot.propertyIds?.length || 0} properties
                            {isMailshotSent(mailshot) && (
                              <span className="mailshot-item__badge">
                                Mailed Out
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedMailshot && (
                    <>
                      <div className="add-check-first">
                        <Checkbox
                          checked={checkFirst}
                          onChange={(e) => setCheckFirst(e.target.checked)}
                          label="Check for doubles and do not contact first"
                          disabled={adding || checking}
                        />
                        <p className="add-check-first__words">
                          Reads these homes against this mailshot's own list, against each other and against every
                          home marked do not contact, and lists what reads as a double before anything is added.
                          About a second for a few thousand homes. The check is this page's: the add itself does
                          not check, so with this unticked everything is added as it always was.
                        </p>
                        {addTimeWords(filteredPropertyIds.length) && (
                          <p className="add-check-first__words" data-testid="add-time">{addTimeWords(filteredPropertyIds.length)}</p>
                        )}
                      </div>

                      {checkFirst && checkProblem && (
                        <InfoNotice variant="warning" data-testid="add-check-problem">
                          The check could not be made: {checkProblem}. Nothing has been added. Try it again with the
                          button below, or add these homes without the check.
                          <div>
                            <button
                              className="mailshot-modal__button"
                              onClick={() => addIds(withoutKnown(filteredPropertyIds), uncheckedWords("failed", keptOut, sentKeptOut, sentAddedUnticked))}
                              disabled={adding || checking || priceBlocked || filteredPropertyIds.length === 0}
                            >
                              Add without the check
                            </button>
                          </div>
                        </InfoNotice>
                      )}

                      {knownDoNotContact.length > 0 && keptOut > 0 && !checked && (
                        <InfoNotice variant="warning" data-testid="known-do-not-contact">
                          {keptOut.toLocaleString("en-GB")} of these homes {keptOut === 1 ? "was" : "were"} found by the check to match do not contact,
                          and {keptOut === 1 ? "stays" : "stay"} out whether the check is ticked or not.
                        </InfoNotice>
                      )}

                      {checked && toAdd.ids.length === 0 && checked.check.counts.listed === 0 && (
                        <InfoNotice variant="info" data-testid="nothing-to-add">
                          There is nothing to add: {[
                            checked.check.counts.alreadyIn > 0 ? `${checked.check.counts.alreadyIn.toLocaleString("en-GB")} of these homes ${checked.check.counts.alreadyIn === 1 ? "is" : "are"} already in this mailshot` : null,
                            toAdd.sentLeftOut > 0 ? `${toAdd.sentLeftOut.toLocaleString("en-GB")} ${toAdd.sentLeftOut === 1 ? "is" : "are"} already in a mailshot that has been sent, and left out` : null,
                          ].filter(Boolean).join("; ") || "no home is left to add"}.
                        </InfoNotice>
                      )}

                      {/* Homes already in a mailshot that has been sent: left out unless unticked. The check says which. */}
                      {sent && sent.count > 0 && (
                        <div className="mailshot-modal__filter" data-testid="sent-choice" style={{ marginTop: "0.75rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                            <Checkbox
                              checked={leaveOutSent}
                              onChange={(e) => setLeaveOutSent(e.target.checked)}
                              label={sent.label}
                              disabled={adding || checking}
                            />
                            <span style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>(On by default: a home already in a mailshot that has been sent is not added to another.)</span>
                            <Button variant="link" size="small" aria-expanded={showSent} onClick={() => setShowSent((shown) => !shown)}>
                              {showSent ? "Hide them" : "Show them"}
                            </Button>
                          </div>
                          {sent.untickedWords && (
                            <InfoNotice variant="warning" data-testid="sent-unticked">{sent.untickedWords}</InfoNotice>
                          )}
                          <p data-testid="sent-by-record" style={{ fontSize: "0.8125rem", color: "#475569", margin: "0.35rem 0 0" }}>{SENT_BY_RECORD}</p>
                          {showSent && (
                            <ul data-testid="sent-list" style={{ fontSize: "0.8125rem", margin: "0.35rem 0 0", paddingLeft: "1.1rem", maxHeight: "9rem", overflow: "auto" }}>
                              {sent.homes.map(sentRow).map((row) => (
                                <li key={row.id}>
                                  {row.address} <span style={{ color: "#6b7280" }}>(in {row.mailshots.join(", ")})</span>{" "}
                                  <a href={row.href} target="_blank" rel="noopener noreferrer">its page</a>
                                  {row.notStamped.length > 0 && (
                                    <div data-testid={`sent-not-stamped-${row.id}`} style={{ color: "#92400e" }}>{NOT_STAMPED_WORDS(row.notStamped)}</div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* The check has been unticked, or is out of date, after it named homes as already sent: they stay
                          out while this is ticked, and the box says so. */}
                      {!checked && sentKnownNow > 0 && (
                        <div className="mailshot-modal__filter" data-testid="known-sent" style={{ marginTop: "0.75rem" }}>
                          <Checkbox
                            checked={leaveOutSent}
                            onChange={(e) => setLeaveOutSent(e.target.checked)}
                            label={`Leave out ${sentKnownNow.toLocaleString("en-GB")} ${sentKnownNow === 1 ? "home" : "homes"} the check found already in a mailshot that has been sent`}
                            disabled={adding || checking}
                          />
                          {!leaveOutSent && (
                            <InfoNotice variant="warning" data-testid="known-sent-unticked">{sentKnownNow.toLocaleString("en-GB")} {sentKnownNow === 1 ? "home" : "homes"} already in a mailshot that has been sent WILL be added.</InfoNotice>
                          )}
                        </div>
                      )}

                      {checked && needsALook(checked) && (
                        <AddCheckReview
                          answer={checked}
                          choices={choices}
                          onChoose={(id, value) => setChoices((c) => {
                            const next = { ...c };
                            if (value === "add" || value === "instead" || value === "out") next[id] = value; else delete next[id];
                            return next;
                          })}
                          onChooseClass={(key, value) => setChoices((c) => chooseClass(checked.check, c, key, value))}
                        />
                      )}

                      <button
                        className="mailshot-modal__button mailshot-modal__button--primary"
                        onClick={handleAddToMailshot}
                        disabled={adding || checking || priceBlocked || filteredPropertyIds.length === 0 || (toAdd !== null && toAdd.ids.length === 0)}
                      >
                        {adding ? (
                          <>
                            <FaSpinner className="spinner" /> Adding...
                          </>
                        ) : checking ? (
                          <>
                            <FaSpinner className="spinner" /> Checking...
                          </>
                        ) : toAdd && toAdd.leftOut > 0 ? (
                          `Add ${toAdd.ids.length.toLocaleString("en-GB")}, leave ${toAdd.leftOut.toLocaleString("en-GB")} out`
                        ) : (
                          "Add to Selected Mailshot"
                        )}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AddToMailshotModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  propertyIds: PropTypes.arrayOf(PropTypes.string),
  properties: PropTypes.arrayOf(PropTypes.object),
  onSuccess: PropTypes.func,
};
